import type { ScannedPage } from './scan-agent';

/**
 * A scanned page the sheet holds until it goes to the job. Paige Scan hands each page over as base64; the sheet
 * decodes it once into Blobs, which live outside the script heap, and previews it through an object URL that is
 * revoked when the page leaves the sheet. Never a `data:` URL: a long scan of 600 dpi colour pages previewed that
 * way grew the tab by megabytes a page until it was reloaded (CapturePoint, 2026-10-04).
 */
export interface HeldPage {
  index: number;
  width: number;
  height: number;
  /** An object URL of the page's JPEG (a black-and-white page's preview); revoked by {@link releasePages}. */
  preview: string;
  /** What is uploaded: a black-and-white page's G4 TIFF, any other page's JPEG. */
  upload: Blob;
}

/** The page's bytes as Blobs and its preview URL; the base64 strings are not kept. */
export function holdPage(page: ScannedPage): HeldPage {
  const jpeg = new Blob([bytesOf(page.jpeg)], { type: 'image/jpeg' });
  const upload = page.tiff ? new Blob([bytesOf(page.tiff)], { type: 'image/tiff' }) : jpeg;
  return {
    index: page.index,
    width: page.width,
    height: page.height,
    preview: URL.createObjectURL(jpeg),
    upload,
  };
}

/** The pages left the sheet (removed, added to the job, or the sheet closed): their previews go. */
export function releasePages(pages: readonly HeldPage[]): void {
  for (const page of pages) URL.revokeObjectURL(page.preview);
}

/**
 * A held page as a File the upload path takes, named by its place in the batch: a black-and-white page as its
 * G4 TIFF (about a tenth of a JPEG; the render and OCR stages read it as they read any scanned TIFF), any other
 * as its JPEG.
 */
export function pageFile(page: HeldPage, position: number): File {
  const name = `page-${String(position).padStart(3, '0')}`;
  return page.upload.type === 'image/tiff'
    ? new File([page.upload], `${name}.tif`, { type: 'image/tiff' })
    : new File([page.upload], `${name}.jpg`, { type: 'image/jpeg' });
}

/** What the capture is called in the queue: the scanner's name and the page count. */
export function scanLabel(scanner: string, pages: number): string {
  return `${scanner}, ${pages.toLocaleString()} page${pages === 1 ? '' : 's'}`;
}

function bytesOf(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
